import { useEffect, useMemo, useRef, useState } from 'react';
import { generatePath, Link, useLocation, useSearchParams } from 'react-router';

import { AppRoutes, THROTTLE_LIMIT } from '@/constants';
import { formatDate } from '@/helpers';
import { useCurrentProfile, useDebounce, useThrottle } from '@/hooks';
import { navigate } from '@/providers';

import {
  CloseIcon,
  InfoCircleIcon,
  Mail01Icon,
  Trash03Icon,
  UserCheck01Icon,
  UserPlus01Icon,
  UserX01Icon,
} from '@/assets/icons';
import {
  Button,
  ConfirmationDialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Loading,
  MultiSelect,
  NotFoundResult,
  Pagination,
  Search,
  SortDirection,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/common';
import { Alert } from '@/lib/ui';

import { UserStatus } from '@/features/user-management/enums';
import {
  getUserStatusIcon,
  getUserStatusLabel,
  getUserStatusVariant,
} from '@/features/user-management/utils';

import { USER_STATUS_OPTIONS } from '@/features/user-management/user-management-list/constants';
import { UserField } from '@/features/user-management/user-management-list/enums';
import {
  useDeactivateUserMutation,
  useDeleteUserMutation,
  useReactivateUserMutation,
  useResendInvitationMutation,
  useUserSearching,
} from '@/features/user-management/user-management-list/hooks';
import {
  IUser,
  IUserSearchingPayload,
} from '@/features/user-management/user-management-list/interfaces';

import { GroupRoles } from './GroupRoles';

export const UserList = () => {
  const [isActivateDialogOpen, setActivateDialogOpen] = useState<boolean>(false);
  const [isDeactivateDialogOpen, setDeactivateDialogOpen] = useState<boolean>(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [selectedUserId, setSelectedUserId] = useState<string>();

  const [searchParams] = useSearchParams();
  const routerLocation = useLocation();
  const pageParam = Number(searchParams.get('page'));
  const initialPage = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;

  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const [pageSize] = useState<number>(10);

  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('search') || '');
  const [statuses, setStatuses] = useState<string[]>(
    searchParams.get('statuses')?.split(',').filter(Boolean) || [],
  );

  const [sortBy, setSortBy] = useState<string>(searchParams.get('sortBy') || UserField.Id);
  const [isDescending, setDescending] = useState<boolean>(searchParams.get('desc') !== 'false');

  const previousFilterKeyRef = useRef<string | null>(null);

  const isFiltered = useMemo(
    () => searchQuery.length > 0 || statuses.length > 0,
    [statuses, searchQuery],
  );

  const onClearFilters = () => {
    navigate(
      {
        pathname: routerLocation.pathname,
        hash: routerLocation.hash,
      },
      { replace: true },
    );
    setSearchQuery('');
    setStatuses([]);
  };

  // Debounce search query
  const debouncedSearchQuery = useDebounce(searchQuery);

  // // Throttle filters and sorting
  const throttledStatuses = useThrottle(statuses, THROTTLE_LIMIT, true);
  const throttledSortBy = useThrottle(sortBy, THROTTLE_LIMIT, true);
  const throttledIsDescending = useThrottle(isDescending, THROTTLE_LIMIT, true);

  // Handle column sorting
  const onSort = (columnKey: string, direction: SortDirection) => {
    if (direction === null) {
      setSortBy('');
      setDescending(true);
    } else {
      setSortBy(columnKey);
      setDescending(direction === 'desc');
    }
  };

  // Build API payload
  const apiPayload = useMemo(() => {
    const payload: IUserSearchingPayload = {
      page: currentPage,
      isDescending: throttledIsDescending ?? true,
    };

    if (debouncedSearchQuery) {
      payload.searchBy = debouncedSearchQuery;
    }

    // Divide by 2 because of how enums are compiled in TS
    const statusCount = Object.keys(UserStatus).length / 2;
    if (
      throttledStatuses &&
      throttledStatuses.length > 0 &&
      throttledStatuses.length < statusCount
    ) {
      payload.statuses = throttledStatuses.map((status) => Number(status) as UserStatus);
    }

    if (throttledSortBy) {
      payload.sortBy = throttledSortBy;
    }

    return payload;
  }, [
    debouncedSearchQuery,
    currentPage,
    throttledStatuses,
    throttledSortBy,
    throttledIsDescending,
  ]);

  // Fetch data from API
  const { data, isLoading, isFetching, refetch } = useUserSearching(apiPayload);
  const deactivateUserMutation = useDeactivateUserMutation();
  const reactivateUserMutation = useReactivateUserMutation();
  const resendInvitationMutation = useResendInvitationMutation();
  const deleteUserMutation = useDeleteUserMutation();
  const { data: currentUser } = useCurrentProfile();

  const users = useMemo(() => data?.data || [], [data?.data]);
  const totalPages = useMemo(() => data?.metadata?.totalPages || 0, [data?.metadata?.totalPages]);
  const totalItems = useMemo(() => data?.metadata?.totalItems || 0, [data?.metadata?.totalItems]);
  const isEmpty = useMemo(() => users.length === 0, [users]);

  const onDeactivateUser = async (userId: string | undefined) => {
    if (!userId) return;
    deactivateUserMutation.mutate(userId, {
      onSuccess: () => {
        Alert.success('User has been deactivated successfully.');
        refetch();
        setDeactivateDialogOpen(false);
        setSelectedUserId(undefined);
      },
    });
  };

  const onReactivateUser = async (userId: string | undefined) => {
    if (!userId) return;
    await reactivateUserMutation.mutate(userId, {
      onSuccess: () => {
        Alert.success('User has been reactivated successfully.');
        refetch();
        setActivateDialogOpen(false);
        setSelectedUserId(undefined);
      },
    });
  };

  const onResendInvitation = async (userId: string) => {
    await resendInvitationMutation.mutate(userId, {
      onSuccess: () => {
        Alert.success('A new invitation email has been sent.');
        refetch();
      },
    });
  };

  const onDeleteUser = async (userId: string | undefined) => {
    if (!userId) return;
    await deleteUserMutation.mutate(userId, {
      onSuccess: () => {
        Alert.success('User has been removed successfully.');
        refetch();
        setDeleteDialogOpen(false);
        setSelectedUserId(undefined);
      },
    });
  };

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();

    if (currentPage > 1) params.set('page', String(currentPage));
    if (searchQuery) params.set('search', searchQuery);
    if (statuses.length) params.set('statuses', statuses.join(','));
    if (sortBy !== UserField.Id) params.set('sortBy', sortBy);
    if (!isDescending) params.set('desc', 'false');

    // Navigate with an explicit location object so react-router keeps the current hash
    navigate(
      {
        pathname: routerLocation.pathname,
        search: params.toString() ? `?${params.toString()}` : '',
        hash: routerLocation.hash,
      },
      { replace: true },
    );
  }, [
    currentPage,
    searchQuery,
    statuses,
    sortBy,
    isDescending,
    routerLocation.hash,
    routerLocation.pathname,
  ]);

  // Reset to page 1 when filters actually change (skip initial mount + throttle settling)
  useEffect(() => {
    const filterKey = JSON.stringify([
      debouncedSearchQuery,
      throttledStatuses,
      throttledSortBy,
      throttledIsDescending,
    ]);

    if (previousFilterKeyRef.current === null) {
      // First run — store baseline, don't reset
      previousFilterKeyRef.current = filterKey;
      return;
    }

    if (previousFilterKeyRef.current !== filterKey) {
      previousFilterKeyRef.current = filterKey;
      setCurrentPage(1);
    }
  }, [debouncedSearchQuery, throttledStatuses, throttledSortBy, throttledIsDescending]);

  return (
    <div>
      <div className="flex justify-between w-full items-start flex-wrap gap-2">
        <div className="flex flex-row flex-wrap justify-start items-center gap-2">
          <Search
            placeholder="Search by user ID, user name and email"
            className="w-[340px] sm:w-[340px] gap-2"
            style={{ paddingRight: '16px' }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <MultiSelect
            options={USER_STATUS_OPTIONS}
            defaultValue={statuses}
            placeholder="Status"
            onValueChange={(values) => {
              setStatuses(values);
            }}
            maxCount={1}
            className="!w-fit !min-w-[280px]"
          />
          {isFiltered && (
            <Button
              buttonColor="primary"
              buttonStyle="textlink"
              prefixIcon={<CloseIcon className="size-5" />}
              onClick={onClearFilters}
              size="default"
            >
              Clear
            </Button>
          )}
        </div>
        <div className="flex flex-row flex-wrap items-start justify-start gap-3">
          <Button
            buttonColor="primary"
            buttonStyle="filled"
            borderRadius="soft"
            prefixIcon={<UserPlus01Icon />}
            onClick={() => {
              navigate(AppRoutes.USER_MANAGEMENT_CREATION);
            }}
          >
            Add User
          </Button>
        </div>
      </div>
      <div className="w-full relative">
        {isEmpty && !isLoading && !isFetching ? (
          <NotFoundResult label="No users." />
        ) : (
          <>
            <div className="w-full pb-3 mb-3 overflow-auto custom-scrollbar">
              <Table className="mt-4 min-w-full !w-fit !overflow-hidden" size="sm" template="zebra">
                <TableHeader>
                  <TableRow>
                    <TableHead
                      sortable
                      sortDirection={
                        sortBy === UserField.Id ? (isDescending ? 'desc' : 'asc') : null
                      }
                      onSort={(direction) => onSort(UserField.Id, direction)}
                    >
                      User ID
                    </TableHead>
                    <TableHead
                      sortable
                      sortDirection={
                        sortBy === UserField.FullName ? (isDescending ? 'desc' : 'asc') : null
                      }
                      onSort={(direction) => onSort(UserField.FullName, direction)}
                    >
                      User Name
                    </TableHead>
                    <TableHead
                      sortable
                      sortDirection={
                        sortBy === UserField.Email ? (isDescending ? 'desc' : 'asc') : null
                      }
                      onSort={(direction) => onSort(UserField.Email, direction)}
                    >
                      Email
                    </TableHead>
                    <TableHead>Roles</TableHead>
                    <TableHead
                      sortable
                      sortDirection={
                        sortBy === UserField.Status ? (isDescending ? 'desc' : 'asc') : null
                      }
                      onSort={(direction) => onSort(UserField.Status, direction)}
                    >
                      Status
                    </TableHead>
                    <TableHead
                      sortable
                      sortDirection={
                        sortBy === UserField.InvitationDate ? (isDescending ? 'desc' : 'asc') : null
                      }
                      onSort={(direction) => onSort(UserField.InvitationDate, direction)}
                    >
                      Invitation Sent Date
                    </TableHead>
                    <TableHead
                      sortable
                      sortDirection={
                        sortBy === UserField.ActivatedDate ? (isDescending ? 'desc' : 'asc') : null
                      }
                      onSort={(direction) => onSort(UserField.ActivatedDate, direction)}
                    >
                      First Activated Date
                    </TableHead>
                    <TableHead
                      sortable
                      sortDirection={
                        sortBy === UserField.LastLoginDate ? (isDescending ? 'desc' : 'asc') : null
                      }
                      onSort={(direction) => onSort(UserField.LastLoginDate, direction)}
                    >
                      Last Login Date
                    </TableHead>
                    <TableHead className="text-right !pr-2">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user: IUser) => (
                    <TableRow key={user.id} rowId={user.id}>
                      <TableCell className="w-[106px] text-neutral-9">{user.id}</TableCell>
                      <TableCell className="font-semibold underline text-primary-base w-[240px]">
                        <Link
                          to={generatePath(AppRoutes.USER_MANAGEMENT_UPDATE, {
                            id: user.id,
                          })}
                          className="block truncate"
                          title={user.fullName}
                        >
                          {user.fullName}
                        </Link>
                      </TableCell>
                      <TableCell className="w-[312px]">
                        <span className="block truncate text-neutral-9" title={user.email}>
                          {user.email}
                        </span>
                      </TableCell>
                      <TableCell className="w-[280px]">
                        <GroupRoles roles={user.roles?.sort()} />
                      </TableCell>
                      <TableCell
                        cellType="status"
                        className="w-[144px]"
                        statusVariant={getUserStatusVariant(user.status)}
                        statusIcon={(() => {
                          const Icon = getUserStatusIcon(user.status);
                          return <Icon className="size-3" />;
                        })()}
                      >
                        {getUserStatusLabel(user.status)}
                      </TableCell>
                      <TableCell className="w-[200px] text-neutral-9">
                        {user.lastInvitationDate ? (
                          formatDate({
                            date: new Date(user.lastInvitationDate),
                            hasTime: true,
                          })
                        ) : (
                          <span className="tracking-[1px]">--</span>
                        )}
                      </TableCell>
                      <TableCell className="w-[200px] text-neutral-9">
                        {user.activatedDate
                          ? formatDate({
                              date: new Date(user.activatedDate),
                              hasTime: true,
                            })
                          : 'Not activated'}
                      </TableCell>
                      <TableCell className="w-[200px] text-neutral-9">
                        {user.lastLoginDate
                          ? formatDate({
                              date: new Date(user.lastLoginDate),
                              hasTime: true,
                            })
                          : 'Never logged in'}
                      </TableCell>
                      <TableCell align="right" cellType="custom" className="!p-0 w-[68px] relative">
                        <DropdownMenu>
                          <DropdownMenuTrigger
                            className="w-[68px] h-full aspect-square absolute top-0 right-0 !rounded-none hover:rounded-none !bg-inherit hover:!bg-neutral-4"
                            asChild
                          ></DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="start"
                            side="left"
                            alignOffset={40}
                            sideOffset={-55}
                            className="rounded-xs"
                          >
                            <DropdownMenuItem
                              className="!rounded-xs"
                              onClick={() =>
                                navigate(
                                  generatePath(AppRoutes.USER_MANAGEMENT_UPDATE, {
                                    id: user.id,
                                  }),
                                )
                              }
                            >
                              <InfoCircleIcon className="size-4 text-neutral-9" />
                              <span className="text-neutral-9">View Details</span>
                            </DropdownMenuItem>
                            {currentUser?.id !== user.id && (
                              <>
                                {user.status === UserStatus.Inactive && (
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setSelectedUserId(user.id);
                                      setActivateDialogOpen(true);
                                    }}
                                  >
                                    <UserCheck01Icon className="size-4 text-neutral-9" />
                                    <span className="text-neutral-9">Activate User</span>
                                  </DropdownMenuItem>
                                )}
                                {user.status === UserStatus.Active && (
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setSelectedUserId(user.id);
                                      setDeactivateDialogOpen(true);
                                    }}
                                  >
                                    <UserX01Icon className="size-4 text-neutral-9" />
                                    <span className="text-neutral-9">Deactivate User</span>
                                  </DropdownMenuItem>
                                )}
                                {user.status === UserStatus.Pending && (
                                  <DropdownMenuItem onClick={() => onResendInvitation(user.id)}>
                                    <Mail01Icon className="size-4 text-neutral-9" />
                                    <span className="text-neutral-9">Resend Invitation</span>
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  onClick={() => {
                                    setSelectedUserId(user.id);
                                    setDeleteDialogOpen(true);
                                  }}
                                >
                                  <Trash03Icon className="size-4 text-red-base" />
                                  <span className="text-red-base">Delete User</span>
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage || 1}
                totalPages={totalPages || 0}
                totalItems={totalItems || 0}
                pageSize={pageSize || 10}
                onPageChange={(page) => setCurrentPage(page || 1)}
              />
            )}
          </>
        )}
        {(isLoading || isFetching) && <Loading fullScreen />}
      </div>
      <ConfirmationDialog
        icon={UserCheck01Icon}
        open={isActivateDialogOpen}
        onOpenChange={setActivateDialogOpen}
        variant="green"
        title="Activate user"
        subtitle="Activating this user will restore their access to the system. Do you want to continue?"
        onConfirm={() => onReactivateUser(selectedUserId)}
        isConfirming={reactivateUserMutation.isPending}
      />
      <ConfirmationDialog
        icon={UserX01Icon}
        open={isDeactivateDialogOpen}
        onOpenChange={setDeactivateDialogOpen}
        variant="red"
        title="Deactivate user"
        subtitle="Deactivating this user will remove their access to the system. Do you want to continue?"
        onConfirm={() => onDeactivateUser(selectedUserId)}
        isConfirming={deactivateUserMutation.isPending}
      />
      <ConfirmationDialog
        icon={Trash03Icon}
        open={isDeleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        variant="red"
        title="Delete user"
        subtitle="Deleting this user will remove their account. This action cannot be undone."
        onConfirm={() => onDeleteUser(selectedUserId)}
        isConfirming={deleteUserMutation.isPending}
      />
    </div>
  );
};
